import { type BaseCardProps, BaseCard } from "@/components/fragments/card/base-card";
import { type FC } from "react";
import { Flex } from "@chakra-ui/react";
import { Icon } from "@/components/essentials/icon";
import { Typography } from "@/components/essentials/typography";
import { type TypographyBreakpointsVariant } from "@/components/essentials/typography/type";
import { Button, type ButtonProps } from "@/components/essentials/button";
import Link, { type LinkProps } from "next/link";
import { type PartiallyPartial } from "@/modules/utils/types";
import { successCardButtonTestId, successCardIconTestId, successCardTextTestId } from "./const";

export const typographyVariant: TypographyBreakpointsVariant = {
  base: "display5",
  sm: "display4",
  md: "display4",
  lg: "display4",
  xl: "display4",
  "2xl": "display4",
};

export interface SuccessCardProps extends PartiallyPartial<BaseCardProps, "children"> {
  readonly text: string;
  readonly buttonText?: string;
  readonly buttonHref?: LinkProps["href"];
  readonly buttonProps?: Omit<ButtonProps, "children">;
}

export const SuccessCard: FC<SuccessCardProps> = ({ text, buttonText, buttonHref, buttonProps, children, ...rest }) => {
  return (
    <BaseCard {...rest}>
      <Flex flexDir="column" alignItems="center">
        <Icon data-testid={successCardIconTestId} as="checkCircle" boxSize="5rem" />
        <Typography as="pre" data-testid={successCardTextTestId} mt="1.25rem" variant={typographyVariant}>
          {text}
        </Typography>
        {children}
        {buttonText === undefined && buttonHref === undefined && buttonProps === undefined ? null : buttonHref ? (
          <Button
            data-testid={successCardButtonTestId}
            as={Link}
            href={buttonHref}
            w="100%"
            mt="3.75rem"
            styleVariant="mono"
            {...buttonProps}
          >
            {buttonText}
          </Button>
        ) : (
          <Button data-testid={successCardButtonTestId} w="100%" mt="3.75rem" styleVariant="mono" {...buttonProps}>
            {buttonText}
          </Button>
        )}
      </Flex>
    </BaseCard>
  );
};
